import { useEffect, useRef, useCallback } from 'react';
import AuthService from '../../../services/AuthService';

interface SSEEvent {
  type: string;
  data: any;
  timestamp: string;
}

interface UseSSEProps {
  patientId: string;
  enabled: boolean;
  onEvent: (event: SSEEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
}

export const useSSE = ({ patientId, enabled, onEvent, onError, onOpen }: UseSSEProps) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const isConnectingRef = useRef(false);
  const currentPatientIdRef = useRef<string>('');
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const onOpenRef = useRef(onOpen);

  // Update refs when callbacks change to avoid stale closures
  useEffect(() => {
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
    onOpenRef.current = onOpen;
  }, [onEvent, onError, onOpen]);


  async function getAuthHeaders() {
    const authService = new AuthService();
    const token = await authService.getAccessToken();
    
    if (!token) {
      throw new Error("No access token available");
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }
  
  const disconnect = useCallback(() => {
    console.log('SSE: Disconnecting for patient:', currentPatientIdRef.current);
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close();
        console.log('SSE: Connection closed for patient:', currentPatientIdRef.current);
      } catch (error) {
        console.error('SSE: Error closing connection:', error);
      }
      eventSourceRef.current = null;
    }
    
    isConnectingRef.current = false;
  }, []);

  const connect = useCallback(async (targetPatientId: string) => {
    if (!enabled || !targetPatientId || isConnectingRef.current) {
      console.log('SSE: Connection not needed or already connecting', { 
        enabled, 
        targetPatientId, 
        isConnecting: isConnectingRef.current 
      });
      return;
    }

    // Don't reconnect if we're already connected to the same patient
    if (eventSourceRef.current && 
        eventSourceRef.current.readyState === EventSource.OPEN && 
        currentPatientIdRef.current === targetPatientId) {
      console.log('SSE: Already connected to patient:', targetPatientId);
      return;
    }

    // Disconnect any existing connection first
    disconnect();

    try {
      isConnectingRef.current = true;
      currentPatientIdRef.current = targetPatientId;
      const headers = await getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", ""); // extract raw token

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const url = `${apiUrl}/molemapping/events/${targetPatientId}?token=${token}`;
      
      console.log('SSE: Connecting to:', url);
      
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = (event) => {
        console.log('SSE: Connection opened for patient:', targetPatientId);
        console.log('SSE: EventSource readyState:', eventSource.readyState);
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
        // Note: Don't call onOpen here yet, wait for connected event
      };

      eventSource.onmessage = (event) => {
        console.log('SSE: Raw message received:', event.data);
        
        try {
          const eventData = JSON.parse(event.data) as SSEEvent;
          console.log('SSE: Successfully parsed event:', eventData);
          
          // Handle connected event specifically to trigger onOpen
          if (eventData.type === 'connected') {
            console.log('SSE: Connected event received for patient:', targetPatientId);
            onOpenRef.current?.(); // This should trigger the "Live" status
            // Also forward the connected event to the main handler
            onEventRef.current(eventData);
          } else if (eventData.type === 'heartbeat') {
            console.log('SSE: Heartbeat received for patient:', targetPatientId, eventData.data);
            // Heartbeats confirm the connection is alive
          } else if (eventData.type === 'error') {
            console.error('SSE: Server error:', eventData.data);
            onErrorRef.current?.(new Event('Server Error'));
          } else {
            console.log('SSE: Processing event:', eventData.type, 'for patient:', targetPatientId);
            onEventRef.current(eventData);
          }
        } catch (error) {
          console.error('SSE: Error parsing event data:', error);
          console.error('SSE: Raw data that failed to parse:', event.data);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE: Connection error for patient:', targetPatientId);
        console.error('SSE: EventSource readyState:', eventSource.readyState);
        console.error('SSE: Error event:', error);
        
        isConnectingRef.current = false;
        onErrorRef.current?.(error);
        
        // Handle different ready states
        if (eventSource.readyState === EventSource.CONNECTING) {
          console.log('SSE: Connection is still trying to connect...');
          return;
        } else if (eventSource.readyState === EventSource.CLOSED) {
          console.log('SSE: Connection was closed');
        }
        
        // Auto-reconnect with exponential backoff only if still enabled
        if (enabled && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.pow(2, reconnectAttempts.current) * 1000;
          console.log(`SSE: Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('SSE: Attempting reconnection...');
            reconnectAttempts.current++;
            connect(targetPatientId);
          }, delay);
        } else {
          console.error('SSE: Max reconnection attempts reached or SSE disabled');
        }
      };

    } catch (error) {
      console.error('SSE: Error creating connection:', error);
      isConnectingRef.current = false;
    }
  }, [enabled, disconnect]);

  useEffect(() => {
    console.log('SSE: useEffect triggered', { patientId, enabled });
    
    if (enabled && patientId) {
      connect(patientId);
    } else {
      disconnect();
    }

    return () => {
      console.log('SSE: Cleanup called');
      disconnect();
    };
  }, [patientId, enabled]); // Remove connect and disconnect from dependencies

  return { connect: () => connect(patientId), disconnect };
};
